import { format, isSameDay, isValid, parse } from "date-fns"
import type { ErroApi } from "./dadosPedidoFornecedor"
import { preencherCoordenadasPedidos } from "./dadosPedidoFornecedor"
import { listarEntregadores } from "./entregadores"
import type { Entregador } from "./entregadores"
// integracao.js, parametros.js e quitacaoAutomatica.js são módulos JS legados (sem tipagem própria).
import {
    buscarInformacoesPedido,
    buscarPedidosListagem,
    selecionarMotoqueiroPedido,
} from "./integracao"
import { obterParametrosGerais, salvarParametro } from "./parametros"
import { possuiRecursoAssinatura } from "./assinatura"
import { RegistrarLogErro } from "./clientefiel"

export type { Entregador, ErroApi }

/**
 * Roteirização = associar visualmente (num mapa) um entregador já cadastrado a
 * um ou mais pedidos de delivery, reutilizando a associação pedido↔entregador
 * que já existe no Recebedor (`integracao/entregadores/selecionar`).
 *
 * O limite comercial (50 pedidos roteirizados POR MÊS) é persistido por
 * estabelecimento na tabela `ParametroGeral`, sem alteração de schema, usando
 * os endpoints REST `parametro/listar` e `parametro/salvar` já disponíveis.
 * O contador guarda o mês vigente no formato "AAAA-MM:contagem" (ex.:
 * "2026-09:37"), então zera sozinho na virada do mês — sem job agendado.
 */

/** Contador mensal de pedidos roteirizados; valor no formato "AAAA-MM:contagem". */
export const PARAM_PEDIDOS_ROTEIRIZADOS = "ROTEIRIZACAO_PEDIDOS_ROTEIRIZADOS"
/** Limite de pedidos roteirizados por mês; ausente => usa o padrão. O comercial ajusta este valor ao ativar a funcionalidade. */
export const PARAM_LIMITE_PEDIDOS = "ROTEIRIZACAO_LIMITE_PEDIDOS"
/** Limite mensal padrão enquanto a funcionalidade não é ativada pelo comercial. */
export const LIMITE_PADRAO_ROTEIRIZACAO = 50

/** Telefone do comercial (mesmo usado no upsell "Contratar" da tela Serviços). */
export const WHATSAPP_COMERCIAL = "5531994754366"

/**
 * Filtro de quais status aparecem no mapa/lista. Mapeamento dos códigos segue
 * o `statusPedido` do app (stores/PedidosStore.ts):
 * - aceitos:    0 (aguardando aceitação), 1, 2 (aceito);
 * - preparo:    7 e demais "em preparação";
 * - entrega:    3, 6;
 * - entregues:  4 (entregue), 5 (pago);
 * - cancelados: status negativos.
 * Os status ocultos do painel (-5, 77, 99) nunca aparecem.
 */
export type GrupoStatusRota = "aceitos" | "preparo" | "entrega" | "entregues" | "cancelados"

export interface FiltroStatusRota {
    aceitos: boolean
    preparo: boolean
    entrega: boolean
    entregues: boolean
    cancelados: boolean
}

/** Padrão: só aceitos, em preparo e em entrega. */
export const FILTRO_STATUS_PADRAO: FiltroStatusRota = {
    aceitos: true,
    preparo: true,
    entrega: true,
    entregues: false,
    cancelados: false,
}

const STATUS_OCULTOS = [-5, 77, 99]

export function grupoDoStatus(status: number): GrupoStatusRota | null {
    if (!Number.isFinite(status)) return null
    if (STATUS_OCULTOS.includes(status)) return null
    if (status < 0) return "cancelados"
    if (status === 4 || status === 5) return "entregues"
    if (status === 3 || status === 6) return "entrega"
    if (status === 0 || status === 1 || status === 2) return "aceitos"
    return "preparo"
}

function statusPermitido(status: number, filtro: FiltroStatusRota): boolean {
    const grupo = grupoDoStatus(status)
    return grupo != null && filtro[grupo]
}

export interface PedidoRoteirizavel {
    id: number
    /** Código curto do pedido (ex.: exibido como #shortReference), com fallback no id. */
    shortReference: string
    cliente: string
    telefone: string | null
    latitude: number | null
    longitude: number | null
    tipoPedido: string
    codigoStatus: number
    status: string
    valorTotal: number
    taxaEntrega: number | null
    /** Nome do entregador já associado ao pedido (ou null se ainda não roteirizado). */
    motoboyPedido: string | null
    dataRealizacao: number
    linkPedido: string
}

export interface UsoRoteirizacao {
    usados: number
    limite: number
    /**
     * true quando a assinatura possui o recurso ROTEIRIZACAO (RecursoAssinatura ativo) —
     * roteirizações ILIMITADAS. Quando true, o limite/contador mensal deixa de ser aplicado.
     */
    ilimitado: boolean
    /** id do registro em ParametroGeral (null se ainda não existe). */
    idParametroContador: number | null
}

interface ParametroGeralDTO {
    id: number
    nome: string
    valor: string
    publico: boolean
}

function ehErro(resposta: unknown): resposta is ErroApi {
    return (
        typeof resposta === "object" &&
        resposta !== null &&
        "retornoErro" in resposta &&
        (resposta as ErroApi).retornoErro > 0
    )
}

/** Converte um valor (pode vir como string do backend) em número finito, ou null. */
export function coordNumerica(valor: unknown): number | null {
    if (valor == null || valor === "") return null
    const n = typeof valor === "number" ? valor : Number(valor)
    return Number.isFinite(n) ? n : null
}

/** Um pedido tem coordenadas válidas (numéricas e != 0) para plotar no mapa? */
export function temCoordenadas(pedido: PedidoRoteirizavel): boolean {
    return (
        typeof pedido.latitude === "number" &&
        Number.isFinite(pedido.latitude) &&
        pedido.latitude !== 0 &&
        typeof pedido.longitude === "number" &&
        Number.isFinite(pedido.longitude) &&
        pedido.longitude !== 0
    )
}

/** Um pedido já foi associado a um entregador? */
export function jaRoteirizado(pedido: PedidoRoteirizavel): boolean {
    return pedido.motoboyPedido != null && pedido.motoboyPedido.trim() !== ""
}

/** Formato de data/hora esperado pelo endpoint de listagem (igual ao Pedidos do Dia). */
const FMT_API = "yyyy-MM-dd HH:mm:ss"
/** Quantos detalhes de pedido buscar em paralelo ao enriquecer coordenadas. */
const CONCORRENCIA_DETALHE = 6

/**
 * Início da janela operacional: as 06:00 mais recentes antes de agora
 * (mesma regra da tela Pedidos do Dia), pois o "dia" do restaurante cruza a
 * meia-noite.
 */
function inicioSeisHoras(): Date {
    const agora = new Date()
    const inicio = new Date(agora)
    inicio.setHours(6, 0, 0, 0)
    if (agora.getHours() < 6) inicio.setDate(inicio.getDate() - 1)
    return inicio
}

/**
 * Janela de consulta a partir da data escolhida (dd/mm/aaaa):
 * - hoje (ou vazio/ inválido) => janela operacional [06:00 mais recente, agora],
 *   idêntica à tela Pedidos do Dia;
 * - dia passado => o dia inteiro [00:00, 23:59:59].
 */
function janelaConsulta(data: string): { dataInicio: string; dataFim: string } {
    const hoje = new Date()
    const dataParseada = data ? parse(data, "dd/MM/yyyy", new Date()) : null
    const ehHoje = !dataParseada || !isValid(dataParseada) || isSameDay(dataParseada, hoje)
    if (ehHoje) {
        return { dataInicio: format(inicioSeisHoras(), FMT_API), dataFim: format(hoje, FMT_API) }
    }
    const inicioDia = new Date(dataParseada)
    inicioDia.setHours(0, 0, 0, 0)
    const fimDia = new Date(dataParseada)
    fimDia.setHours(23, 59, 59, 999)
    return { dataInicio: format(inicioDia, FMT_API), dataFim: format(fimDia, FMT_API) }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
function jaTemCoordenadas(item: any): boolean {
    const c = item?.deliveryAddress?.coordinates
    return !!c && c.latitude != null && c.longitude != null
}

/**
 * Garante o detalhe completo do pedido (com coordenadas e entregador). Se a
 * listagem já vier no shape completo (DetalhesPedidoRetorno), usa direto; senão
 * busca o detalhe em `integracao/orders/{id}`.
 */
async function garantirDetalhe(item: any, authorization: string): Promise<any> {
    if (jaTemCoordenadas(item)) return item
    const info = await buscarInformacoesPedido(authorization, item.id)
    if (info && !info.retornoErro && info.id != null) {
        return { ...item, ...info }
    }
    return item
}

async function enriquecerComDetalhe(itens: any[], authorization: string): Promise<any[]> {
    const resultado: any[] = []
    for (let i = 0; i < itens.length; i += CONCORRENCIA_DETALHE) {
        const lote = itens.slice(i, i + CONCORRENCIA_DETALHE)
        const detalhes = await Promise.all(lote.map(it => garantirDetalhe(it, authorization)))
        resultado.push(...detalhes)
    }
    return resultado
}

function mapearPedidoDetalhe(o: any): PedidoRoteirizavel | null {
    if (!o || o.id == null) return null
    const coord = o.deliveryAddress?.coordinates ?? {}
    const referencia = o.shortReference ?? o.reference ?? o.id
    return {
        id: Number(o.id),
        shortReference: String(referencia),
        cliente: o.customer?.name ?? o.cliente ?? "Cliente",
        telefone: o.customer?.phone ?? o.telefone ?? null,
        latitude: coordNumerica(coord.latitude ?? o.latitude),
        longitude: coordNumerica(coord.longitude ?? o.longitude),
        tipoPedido: String(o.type ?? o.tipoPedido ?? "0"),
        codigoStatus: Number(o.status ?? o.codigoStatus ?? 0),
        status: String(o.descricaoStatus ?? o.statusDescricao ?? o.status ?? ""),
        valorTotal: Number(o.totalPrice ?? o.valorTotal ?? 0),
        taxaEntrega: o.deliveryFee ?? o.taxaEntrega ?? null,
        motoboyPedido: o.nomeEntregador ?? o.motoboyPedido ?? null,
        dataRealizacao: Number(o.date ?? o.dataRealizacao ?? 0),
        linkPedido: o.linkPedido ?? "",
    }
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * Busca os pedidos de delivery do dia para roteirizar, ESPELHANDO a tela
 * Pedidos do Dia: `buscarPedidosListagem` na janela operacional (06:00) e, se
 * necessário, enriquece cada pedido com o detalhe para obter latitude/longitude
 * e o entregador já associado. O `filtro` decide quais status aparecem — só os
 * status pedidos são enriquecidos (não gastamos detalhe com o que está oculto).
 */
export const buscarPedidosParaRoteirizar = async function (
    authorization: string,
    idEstabelecimento: number | string,
    data: string,
    filtro: FiltroStatusRota
): Promise<PedidoRoteirizavel[] | ErroApi> {
    if (!idEstabelecimento) {
        return { retornoErro: 1, mensagem: "Estabelecimento não identificado." }
    }

    const { dataInicio, dataFim } = janelaConsulta(data)
    const resposta = await buscarPedidosListagem(
        idEstabelecimento,
        { campoData: "data", dataInicio, dataFim, tiposPedido: ["0"], page: 1, size: 500 },
        authorization
    )
    if (resposta.retornoErro) {
        return { retornoErro: resposta.retornoErro, mensagem: resposta.mensagem || "Erro ao buscar pedidos." }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const listagem: any[] = (resposta.pedidos ?? []).filter((p: any) => {
        const s = Number(p.status)
        if (!Number.isFinite(s)) return true // sem status na listagem: mantém e resolve no detalhe
        return statusPermitido(s, filtro)
    })

    const detalhados = await enriquecerComDetalhe(listagem, authorization)
    const pedidos = detalhados
        .map(mapearPedidoDetalhe)
        .filter((p): p is PedidoRoteirizavel => p !== null)
        .filter(p => p.tipoPedido === "0")
        .filter(p => statusPermitido(p.codigoStatus, filtro))

    // Fallback de coordenadas: pedidos de delivery cujo endereço nunca foi geocodificado
    // chegam sem lat/long (o detalhe lê do mesmo Endereço). Pede ao backend para geocodificar
    // e PERSISTIR esses endereços de uma vez, e aplica o resultado, para que caiam no mapa.
    await preencherCoordenadasFaltantes(pedidos, authorization)
    return pedidos
}

/**
 * Para os pedidos que ainda estão sem coordenada válida, chama o backend
 * (`pedido/preencher-coordenadas`) para geocodificar+persistir e aplica o retorno
 * in-place. Sem coordenada para resolver, ou em caso de falha, os pedidos apenas
 * seguem sem pino — nunca derruba a listagem.
 */
async function preencherCoordenadasFaltantes(
    pedidos: PedidoRoteirizavel[],
    authorization: string
): Promise<void> {
    const semCoordenada = pedidos.filter(p => !temCoordenadas(p))
    if (!semCoordenada.length) return

    const coords = await preencherCoordenadasPedidos(
        authorization,
        semCoordenada.map(p => p.id)
    )
    if (!coords.length) return

    const porId = new Map(coords.map(c => [c.id, c]))
    for (const pedido of pedidos) {
        const c = porId.get(pedido.id)
        if (c) {
            pedido.latitude = coordNumerica(c.latitude)
            pedido.longitude = coordNumerica(c.longitude)
        }
    }
}

/** Entregadores cadastrados (Motoboy) do estabelecimento, apenas os ativos. */
export const listarEntregadoresAtivos = async function (
    authorization: string
): Promise<Entregador[] | ErroApi> {
    const resposta = await listarEntregadores(authorization)
    if ("retornoErro" in resposta) {
        return resposta
    }
    return resposta.filter(e => e.ativo)
}

/**
 * Associa um entregador (cadastro) a um pedido, reutilizando exatamente a mesma
 * chamada do Recebedor. O backend resolve o entregador pelo id do Motoboy.
 */
export const associarEntregadorAoPedido = async function (
    idPedido: number,
    idEntregador: number,
    authorization: string
): Promise<true | ErroApi> {
    const resposta = await selecionarMotoqueiroPedido(idPedido, idEntregador, authorization)
    if (!resposta) {
        return { retornoErro: 1, mensagem: "Não foi possível associar o entregador ao pedido." }
    }
    if (ehErro(resposta)) {
        return resposta
    }
    return true
}

/** Chave do mês vigente, no formato "AAAA-MM". */
export function mesAtual(): string {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

/**
 * Interpreta o valor do contador ("AAAA-MM:contagem"). Se o mês guardado não é
 * o mês vigente (ou o valor é legado/ausente), a contagem do mês é 0.
 */
function contagemDoMes(valor: string | null | undefined): number {
    if (!valor) return 0
    const separador = valor.indexOf(":")
    if (separador === -1) return 0 // formato legado (sem mês) => reinicia
    const mes = valor.slice(0, separador)
    if (mes !== mesAtual()) return 0
    return Number.parseInt(valor.slice(separador + 1), 10) || 0
}

/** Lê o uso do MÊS vigente (pedidos roteirizados / limite) do estabelecimento. */
export const obterUsoRoteirizacao = async function (
    idEstabelecimento: number | string,
    authorization: string
): Promise<UsoRoteirizacao | ErroApi> {
    try {
        // O recurso ROTEIRIZACAO na assinatura é a FONTE ÚNICA do "ilimitado". Consultado em
        // paralelo; resiliente (falha => false => mantém o limite mensal atual de 50).
        const [resposta, ilimitado] = await Promise.all([
            obterParametrosGerais(idEstabelecimento, authorization),
            possuiRecursoAssinatura(authorization, "ROTEIRIZACAO"),
        ])
        if (ehErro(resposta)) {
            return resposta
        }
        const lista: ParametroGeralDTO[] = Array.isArray(resposta) ? resposta : []
        const contador = lista.find(p => p.nome === PARAM_PEDIDOS_ROTEIRIZADOS)
        const limiteParam = lista.find(p => p.nome === PARAM_LIMITE_PEDIDOS)

        const usados = contagemDoMes(contador?.valor)
        const limite = limiteParam
            ? Number.parseInt(limiteParam.valor, 10) || LIMITE_PADRAO_ROTEIRIZACAO
            : LIMITE_PADRAO_ROTEIRIZACAO

        return {
            usados,
            limite,
            ilimitado,
            idParametroContador: contador ? contador.id : null,
        }
    } catch (error) {
        RegistrarLogErro(error, "obterUsoRoteirizacao", String(idEstabelecimento))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}

/**
 * Persiste o total de pedidos roteirizados NO MÊS em ParametroGeral, no formato
 * "AAAA-MM:contagem". Faz update no lugar quando o registro já existe (passando
 * o id) para não duplicar linhas. Retorna o id do registro salvo.
 */
export const salvarUsoRoteirizacao = async function (
    idEstabelecimento: number | string,
    idParametroContador: number | null,
    novoTotal: number,
    authorization: string
): Promise<{ idParametroContador: number | null } | ErroApi> {
    try {
        const dados: { nome: string; valor: string; publico: boolean; id?: number } = {
            nome: PARAM_PEDIDOS_ROTEIRIZADOS,
            valor: `${mesAtual()}:${novoTotal}`,
            publico: false,
        }
        if (idParametroContador) {
            dados.id = idParametroContador
        }
        const resposta = await salvarParametro(idEstabelecimento, dados, authorization)
        if (ehErro(resposta)) {
            return resposta
        }
        const salvo = resposta as ParametroGeralDTO | null
        return {
            idParametroContador: salvo && salvo.id ? salvo.id : idParametroContador,
        }
    } catch (error) {
        RegistrarLogErro(error, "salvarUsoRoteirizacao", String(idEstabelecimento))
        return { retornoErro: 1, mensagem: (error as Error).message }
    }
}
